import {useCallback} from 'react';
import {Add2WishlistEvent, Log} from '@aboutyou/mlm';
import {getAxios, startSpan} from '@aboutyou/utils';
import {
  BAPI_WISHLIST_SETS,
  WISHLIST_FULL_SET,
  WISHLIST_MINIMAL_SET,
} from '../../utils/sets/wishlist.sets';
import {useLegacyConfig} from '@aboutyou/config';
import {
  useGetProductIsInWishlist,
  useGetWishlistItemForProduct,
  useWishlistSession,
} from './wishlist.selector';
import {basketWithQueryParameter} from '@aboutyou/backbone/endpoints/basket/getBasket';
import {useDefaultHeadersWithToken} from '../../utils/useDefaultHeadersWithToken';
import {
  AddToWishlistError,
  RemoveFromWishlistError,
  WishlistLoadError,
} from './errors';
import {WishlistResponseData} from '@aboutyou/backbone/endpoints/wishlist/getWishlist';
import {useMutation, useRequest} from '@aboutyou/data-fetcher';
import {createDataStore} from '../createDataStore';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/products/v1/product.types';
// eslint-disable-next-line no-restricted-imports, import/no-extraneous-dependencies, monorepo/no-internal-import
import {useCampaignKeyCallback} from '@aboutyou/logic-components/src/backend-services/Campaigns/campaign-state/campaign.hooks';
import {usePersistSessionStateCallback} from '../sessionStore/session.hooks';
import {useGlobalWishlistRefetchToken} from './useGlobalWishlistRefetchToken';
import {notifyWishlistUpdate} from '../../global-basket-wishlist-refetch-tokens';

const HTTP_STATUS = {
  PAYLOAD_TOO_LARGE: 413,
};

let FULL_WISHLIST_PRODUCT_IDS: number[] = [];

interface WishlistState extends WishlistResponseData {
  productIds: number[];
}

const useFetchWishlistCallback = (
  session: ReturnType<typeof useWishlistSession>['session'],
  campaignKey: ReturnType<typeof useWishlistSession>['campaignKey'],
  isSessionLoading: boolean,
) => {
  const {
    shopId,
    wishlist: {GET: getEndpoint},
  } = useLegacyConfig();
  const headers = useDefaultHeadersWithToken(session);

  return useCallback(async (): Promise<WishlistState> => {
    // If we don't have a session and we're not waiting for it to load anymore, we can fall back to the default empty wishlist state
    if (!session && !isSessionLoading) {
      return {
        key: '',
        items: [],
        productIds: [],
      };
    }
    const response = await (
      await getAxios()
    ).get<WishlistResponseData>(getEndpoint, {
      params: {
        with: basketWithQueryParameter(
          BAPI_WISHLIST_SETS[WISHLIST_FULL_SET],
        ).join(','),
        shopId,
        campaignKey,
        // sId is required for mAPI basket/wishlist calls
        sId: session?.resolvedId,
      },
      headers,
    });
    if (response.status !== 200) {
      throw new WishlistLoadError(response.status);
    }

    return {
      ...response.data,
      productIds: getProductIdsFromWishlistResponse(response.data),
    };
  }, [session, isSessionLoading, getEndpoint, shopId, campaignKey, headers]);
};

/**
 * Do not export this hook. Use useWishlist instead!
 */
const useExpensiveWishlist = () => {
  const {session, cacheKey, campaignKey, isLoading} = useWishlistSession();
  const fetchWishlistCallback = useFetchWishlistCallback(
    session,
    campaignKey,
    isLoading,
  );
  const persistSessionState = usePersistSessionStateCallback();

  return useRequest(cacheKey, fetchWishlistCallback, {
    disabled: isLoading,
    refetchToken: useGlobalWishlistRefetchToken(),
    onSuccess: (wishlist?: WishlistState) => {
      const hasItemsInWishlist = Boolean(wishlist?.items?.length);
      // we want to delay persisting of the session state as much as possible for legal reasons
      // if an anonymous user doesn't have anything in the basket/basket/or is loggedIn, we don't need to persist the session-id
      if (hasItemsInWishlist) {
        persistSessionState();
      }
    },
  });
};

export enum UseToggleProductInWishlistResults {
  REMOVED = 'REMOVED',
  ADDED = 'ADDED',
}

export const useToggleProductInWishlist = (
  tracker: (action: 'add-to-wishlist' | 'removed-from-wishlist') => void,
  /**
   * @deprecated
   */
  setShowToast?: (toastState: boolean) => void,
) => {
  const getProductIsInWishlist = useGetProductIsInWishlist();
  const removeProductFromWishlist = useRemoveProductFromWishlistMutation();
  const addProductToWishlist = useAddProductToWishlistMutation();
  const {data: wishlist, updateCache} = useWishlist();
  const wishlistTracker = tracker;

  return useCallback(
    async ({
      productId,
      customData,
    }: {
      productId: Product['id'];
      customData?: unknown;
    }) => {
      const exists = getProductIsInWishlist({productId});

      if (exists) {
        if (!addProductToWishlist.isLoading) {
          removeProductFromWishlist
            .mutateAsync({productId})
            .then(() => {
              FULL_WISHLIST_PRODUCT_IDS = [];
            })
            .finally(() => {
              notifyWishlistUpdate();
            });
          wishlistTracker('removed-from-wishlist');
          return UseToggleProductInWishlistResults.REMOVED;
        }
      } else {
        if (!removeProductFromWishlist.isLoading) {
          addProductToWishlist
            .mutateAsync({productId, customData})
            .then(() => {
              FULL_WISHLIST_PRODUCT_IDS = [];
            })
            .catch((error: Error) => {
              if (
                error.name === 'AddToWishlistError' &&
                error.message === String(HTTP_STATUS.PAYLOAD_TOO_LARGE)
              ) {
                FULL_WISHLIST_PRODUCT_IDS.push(productId);
                setShowToast?.(true);
                wishlistTracker('removed-from-wishlist');
                // remove optimistically added products from cache since those were not actually added
                updateCache({
                  ...wishlist!,
                  productIds:
                    wishlist?.productIds.filter(
                      id => !FULL_WISHLIST_PRODUCT_IDS.includes(id),
                    ) ?? [],
                });
              } else {
                throw error;
              }
            })
            .finally(() => {
              notifyWishlistUpdate();
            });

          wishlistTracker('add-to-wishlist');
          return UseToggleProductInWishlistResults.ADDED;
        }
      }
    },
    [
      addProductToWishlist,
      getProductIsInWishlist,
      removeProductFromWishlist,
      setShowToast,
      updateCache,
      wishlist,
      wishlistTracker,
    ],
  );
};

const {Provider, useData: useWishlist} = createDataStore<WishlistState>({
  defaultValue: undefined,
  useQueryHook: useExpensiveWishlist,
});

export {Provider as WishlistProvider, useWishlist};

const useAddProductToWishlistMutation = () => {
  const {
    shopId,
    wishlist: {POST: addEndpoint},
  } = useLegacyConfig();
  const {session} = useWishlistSession();
  const {data: wishlist, updateCache} = useWishlist();
  const requestCampaignKey = useCampaignKeyCallback();
  const headers = useDefaultHeadersWithToken(session);

  return useMutation(
    async ({
      productId,
      customData = {},
    }: {
      productId: number;
      customData?: unknown;
    }) => {
      if (!session) {
        return;
      }
      const sId = session.resolvedId;
      // optimistically add the productId to the cache
      updateCache({
        ...wishlist!,
        productIds: [...(wishlist?.productIds ?? []), productId],
      });

      const axios = await getAxios();
      const request = axios.post<WishlistResponseData>(
        addEndpoint,
        {productId, customData, shopId},
        {
          params: {
            with: basketWithQueryParameter(
              BAPI_WISHLIST_SETS[WISHLIST_FULL_SET],
            ).join(','),
            shopId,
            campaignKey: await requestCampaignKey().catch(() => undefined),
            // sId is required for mAPI basket/wishlist calls
            sId,
          },
          headers,
          validateStatus: status => status === 201,
        },
      );

      const endAdd2WishlistSpan = startSpan();

      await request
        .then(response => {
          updateCache({
            ...response.data,
            productIds: getProductIdsFromWishlistResponse(response.data),
          });
          Log.compare({
            actionName: Add2WishlistEvent,
            success: true,
            attributes: {
              code: response.status,
              productId,
              variantId: shopId,
              duration: endAdd2WishlistSpan(),
              sId,
            },
          });
        })
        .catch(error => {
          if (error.response) {
            Log.compare({
              actionName: Add2WishlistEvent,
              success: false,
              attributes: {
                code: error.response.status,
                statusText: error.response.statusText,
                productId,
                variantId: shopId,
                duration: endAdd2WishlistSpan(),
                sId,
              },
            });
            throw new AddToWishlistError(error.response.status);
          } else {
            Log.compare({
              actionName: Add2WishlistEvent,
              success: false,
              attributes: {
                code: 0,
                message: error.message,
                productId,
                variantId: shopId,
                duration: endAdd2WishlistSpan(),
                sId,
              },
            });
            throw new AddToWishlistError(error.message);
          }
        });
    },
  );
};

export const useRemoveProductFromWishlistMutation = () => {
  const {
    shopId,
    wishlist: {DELETE: deleteEndpoint},
  } = useLegacyConfig();
  const {session} = useWishlistSession();
  const getWishlistItemForProduct = useGetWishlistItemForProduct();
  const {data: wishlist, updateCache} = useWishlist();
  const requestCampaignKey = useCampaignKeyCallback();
  const headers = useDefaultHeadersWithToken(session);

  return useMutation(async ({productId}: {productId: number}) => {
    if (!session) {
      return;
    }
    const itemToRemove = getWishlistItemForProduct({productId});
    if (!itemToRemove) {
      throw new Error(`Wishlist Item to Remove not found (${productId})`);
    }
    const endpoint = `${deleteEndpoint.endsWith(`/`) ? '' : '/'}${
      itemToRemove.key
    }`;

    // Optimistically remove the productId from the cache
    updateCache({
      ...wishlist!,
      productIds: wishlist?.productIds.filter(id => id !== productId) ?? [],
      items: wishlist?.items.filter(({key}) => key !== itemToRemove.key) ?? [],
    });

    const response = await (
      await getAxios(deleteEndpoint)
    ).delete(endpoint, {
      params: {
        with: basketWithQueryParameter(
          BAPI_WISHLIST_SETS[WISHLIST_MINIMAL_SET],
        ).join(','),
        shopId,
        campaignKey: await requestCampaignKey().catch(() => undefined),
        // sId is required for mAPI basket/wishlist calls
        sId: session.resolvedId,
      },
      headers,
    });

    if (response.status !== 200) {
      updateCache({
        ...wishlist!,
        productIds: wishlist?.productIds || [],
        items: wishlist?.items || [],
      });
      throw new RemoveFromWishlistError(response.status);
    }
  });
};

export const useWishlistMutation = () => {
  const {updateCache} = useWishlist();

  return useMutation(async (wishlistState: WishlistResponseData) => {
    updateCache({
      ...wishlistState,
      productIds: getProductIdsFromWishlistResponse(wishlistState),
    });
  });
};

export const getProductIdsFromWishlistResponse = (
  wishlistResponse?: WishlistResponseData,
) =>
  wishlistResponse?.items
    ?.map(item => item.product?.id)
    .filter((id): id is number => !!id)
    .sort((a, b) => a - b) ?? [];
